import React from 'react';
import { Handle, Position } from 'reactflow';
import type { PeerDagData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { getPaletteEntry } from '../../statusPalette';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useDagNodeOpacity } from '../../../../contexts/ChatDagRuntimeContext';
import HoverCard from '../../../common/HoverCard';
import DagNodeHoverContent from './DagNodeHoverContent';
import { MarkdownPreview } from '../MarkdownPreview';
import { CopyChip } from '../CopyChip';
import {
  HOVER_LIFT_CLASS,
  SELECTION_RING_CLASS,
} from '../chatDagTokens';

// Peer fan-out child for consult_peers.
//
// Shares the status palette with bubble pills + panel rows so a
// failing peer looks the same everywhere. v0.2: 2-line response
// preview via line-clamp + slightly larger body text for legibility
// inside the polar fan positions (peers orbit the parent tool_call
// on an arc, see utils/chatDagLayout.ts).

function formatDuration(ms?: number): string | null {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function formatTokens(n?: number): string | null {
  if (n == null || n <= 0) return null;
  if (n < 1000) return `${n} tok`;
  return `${(n / 1000).toFixed(1)}k tok`;
}

function formatCost(usd?: number): string | null {
  if (usd == null || usd <= 0) return null;
  if (usd < 0.001) return `$${usd.toFixed(5)}`;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

export const PeerNodeCompact: React.FC<{ data: PeerDagData; selected?: boolean }> = ({ data, selected }) => {
  const { darkMode } = useTheme();
  const { focusBubble } = usePersonaChatUI();
  const palette = getPaletteEntry(data.status);
  const messageId = data.messageId;
  const onClick = messageId
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        focusBubble(messageId);
      }
    : undefined;
  const duration = formatDuration(data.durationMs);
  // Defensive: the processor normalizes `response` to string at the
  // boundary, but if a future code path bypasses that path we don't
  // want to crash the bubble. typeof-guard + coerce.
  const body = typeof data.response === 'string' ? data.response : '';
  const name = data.personaName ?? 'Unknown peer';
  const isOk = (data.status ?? 'ok') === 'ok';
  const runState = data.runState ?? 'completed';
  const running = runState === 'running';
  const opacityStyle = useDagNodeOpacity(data.turnKey, running);
  // Prompt preview — italic 1-line at top of card so the operator can
  // confirm at a glance that the persona passed the appropriate prompt
  // to consult_peers (verification ask). Falls back to context if
  // prompt is empty (rare).
  const promptPreview =
    typeof data.prompt === 'string' && data.prompt.trim().length > 0
      ? data.prompt.trim()
      : typeof data.context === 'string' && data.context.trim().length > 0
        ? data.context.trim()
        : null;
  // Stats line — only render if we have useful numbers. tok / cost
  // come from the peer's usage block.
  const tokStr = formatTokens(data.totalTokens);
  const costStr = formatCost(data.costUsd);
  const showStats = tokStr || costStr;

  return (
    <HoverCard
      maxWidth={380}
      content={<DagNodeHoverContent data={data} />}
    >
      <div
        onClick={onClick}
        className={`group relative px-2.5 py-2 rounded-md border w-[240px] shadow-sm ${HOVER_LIFT_CLASS} ${
          darkMode ? 'bg-gray-800/70 border-gray-600/70 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
        } ${running ? 'chatdag-peer-running-ring' : ''} ${selected ? SELECTION_RING_CLASS : ''} ${onClick ? 'cursor-pointer' : ''}`}
        style={opacityStyle}
      >
        {body && <CopyChip text={body} label="Copy peer response" />}
        <Handle
          type="target"
          position={Position.Left}
          id="l"
          className="!opacity-0 !pointer-events-none"
        />

        <div className="flex items-center gap-1.5">
          <span className={`${palette.color} text-sm`}>{palette.icon}</span>
          <span className="font-semibold text-xs truncate flex-1" title={name}>
            {name.replace(/_/g, ' ')}
          </span>
          {duration && <span className="opacity-60 font-mono text-[10px]">{duration}</span>}
        </div>

        {!isOk && palette.label && (
          <div className={`mt-0.5 text-[10px] font-medium ${palette.color}`}>
            {palette.label}
          </div>
        )}

        {promptPreview && (
          <div
            className={`mt-1 text-[10px] italic truncate ${
              darkMode ? 'text-emerald-300/80' : 'text-emerald-700/80'
            }`}
            title={promptPreview}
          >
            <span className="opacity-60">asks:</span> {promptPreview}
          </div>
        )}

        {body && (
          <div className="mt-1">
            <div
              className={`text-[10px] italic leading-tight ${
                darkMode ? 'text-cyan-300/70' : 'text-cyan-700/70'
              }`}
            >
              says:
            </div>
            <div className="mt-0.5 opacity-90">
              <MarkdownPreview
                text={body}
                maxLines={4}
                textSizeClass="text-[11px]"
              />
            </div>
          </div>
        )}

        {showStats && (
          <div
            className={`mt-1 text-[9px] font-mono flex items-center gap-2 ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          >
            {tokStr && <span>{tokStr}</span>}
            {costStr && <span>{costStr}</span>}
          </div>
        )}
      </div>
    </HoverCard>
  );
};

export default PeerNodeCompact;
